import { Injectable } from "@angular/core";
import { BehaviorSubject } from "rxjs";
import { PermissionService } from "./permission.service";
import { LocalStorageService } from "./storage/local-storage.service";
import { UserDTO } from "./auth/User";
import { AccountRelatedDTO } from "./accounts/Account";
import { FacilityDTO } from "./facilities/Facility";
import { AvailableAccount } from "./accounts/AvailableAccount";
import { AccountAppDTO } from "./apps/AccountApp";
import { AccountUserGroupDTO } from "./account-user-group/AccountUserGroup";

@Injectable({
  providedIn: "root",
})
export class SessionService {
  currentUserSubject = new BehaviorSubject<UserDTO | null>(null);
  currentUser = this.currentUserSubject.asObservable();

  currentAccountSubject = new BehaviorSubject<AvailableAccount | null>(null);
  currentAccount = this.currentAccountSubject.asObservable();

  currentAppSubject = new BehaviorSubject<AccountAppDTO | null>(null);
  currentApp = this.currentAppSubject.asObservable();

  facilitiesSubject = new BehaviorSubject<FacilityDTO[] | null>(null);
  facilities = this.facilitiesSubject.asObservable();
  availableAccounts: AvailableAccount[] | undefined;

  constructor(
    private PermissionService: PermissionService,
    private LocalStorageService: LocalStorageService,
  ) {}

  get currentAccountValue() {
    return this.currentAccountSubject.value;
  }

  get currentUserValue() {
    return this.currentUserSubject.value;
  }

  get facilitiesValue() {
    return this.facilitiesSubject.value;
  }

  get selectedFacilities() {
    return this.facilitiesValue?.filter(
      (facility: FacilityDTO) => facility.selected,
    );
  }

  get apps() {
    return this.currentAccountValue?.apps;
  }

  get currentAppValue() {
    return this.currentAppSubject.value;
  }

  get accounts() {
    return this.currentUserValue?.accounts;
  }

  get isSuper() {
    return this.currentUserValue?.super;
  }

  setUser(user: UserDTO) {
    if (!user.authToken) {
      user.authToken = this.LocalStorageService.authToken as string;
    }
    this.PermissionService.clearRole();
    this.LocalStorageService.setUser(user);
    this.currentUserSubject.next(user);
    this.PermissionService.initRoles(user);
    this.initializeAccountUsers();
  }

  updateCurrentUser(user: UserDTO) {
    this.currentUserSubject.next(user);
  }

  changeAccount(availableAccount: AvailableAccount) {
    this.clearAccount();
    this.setCurrentAccount(availableAccount);
  }

  setCurrentAccount(availableAccount: AvailableAccount) {
    this.LocalStorageService.setAccount(availableAccount);
    this.currentAccountSubject.next(availableAccount);
    this.PermissionService.setRole(availableAccount, this.currentUserValue!);

    this.currentAccountValue!.apps.sort(
      (a: AccountAppDTO, b: AccountAppDTO) => {
        return a.name.toLowerCase().localeCompare(b.name.toLowerCase());
      },
    );

    this.initializeFacilities(availableAccount);
    this.initializeApp(availableAccount);
  }

  updateAccounts(userDTO: UserDTO) {
    this.currentUserValue!.accounts = userDTO.accounts;
    this.currentUserValue!.accountUsers = userDTO.accountUsers;
    this.initializeAccountUsers();

    const currentAccount = this.availableAccounts?.find((account) => {
      account.accountID === this.currentAccountValue?.accountID;
    });

    if (currentAccount) {
      this.currentAccountSubject.next(currentAccount);
    } else {
      if (this.availableAccounts?.length) {
        this.currentAccountSubject.next(this.availableAccounts[0]);
      } else {
        console.log("No available accounts to switch to");
      }
    }
  }

  updateUserRoles(userDTO: UserDTO) {
    this.PermissionService.initRoles(userDTO);

    //Update all the accounts roles when a role updates
    userDTO.accounts?.forEach((account) => {
      const availAccount = this.availableAccounts?.find((availableAccount) => {
        return availableAccount.accountID === account.id;
      });
      if (availAccount) {
        availAccount.availableRoles = account.roles;
      }
    });

    this.PermissionService.setRole(
      this.currentAccountValue!,
      this.currentUserValue!,
    );
  }

  clearAccount() {
    this.PermissionService.clearRole();
    this.LocalStorageService.clearAccount();
    this.currentAccountSubject.next(null);
    this.clearApp();
    this.clearFacilities();
  }

  setApp(app: AccountAppDTO) {
    this.LocalStorageService.setApp(app);
    this.currentAppSubject.next(app);
  }

  clearApp() {
    this.LocalStorageService.clearApp();
    this.currentAppSubject.next(null);
  }

  clearFacilities() {
    this.LocalStorageService.clearSelectedFacilities();
    this.facilitiesSubject.next(null);
  }

  logout() {
    this.LocalStorageService.clearUser();
    this.currentUserSubject.next(null);

    this.clearAccount();
  }

  initializeAccountUsers() {
    this.availableAccounts = this.currentUserValue?.accounts.map(
      (account: AccountRelatedDTO) => {
        const accountUser = this.currentUserValue?.accountUsers.find(
          (accountUser) => accountUser.account_id === account.id,
        );
        return new AvailableAccount(account, accountUser);
      },
    );

    this.availableAccounts?.sort((a: AvailableAccount, b: AvailableAccount) => {
      return a.name.toLowerCase().localeCompare(b.name.toLowerCase());
    });
  }

  initializeFacilities(account: AvailableAccount) {
    let facilities: FacilityDTO[] = [];

    //Sort facilities alphabetically
    account.facilities.sort((a: FacilityDTO, b: FacilityDTO) => {
      return a.name.toLowerCase().localeCompare(b.name.toLowerCase());
    });

    if (account.accountUserDTO?.restrict_to_facilities) {
      //Only show the facilities the account user is restricted to
      facilities = account.facilities.filter((facility) => {
        return account.accountUserDTO?.restrict_to_facilities?.includes(
          facility.id,
        );
      });
    } else {
      facilities = account.facilities;
    }
    if (this.LocalStorageService.selectedFacilities) {
      for (const facilityID of this.LocalStorageService.selectedFacilities) {
        const currFacility = facilities.find(
          (facility) => facility.id === facilityID,
        );
        if (currFacility) {
          currFacility.selected = true;
        }
      }
    } else {
      for (const facility of facilities) {
        facility.selected = true;
      }
    }
    //If only one facility available force it to be selected
    if (facilities.length == 1) {
      facilities[0].selected = true;
    }
    this.facilitiesSubject.next(facilities);
    this.setSelectedFacilities();
  }

  updateFacilities(facilities: FacilityDTO[]) {
    //Maintain selected status
    facilities.forEach((facility) => {
      facility.selected = !!this.currentAccountValue!.facilities.find(
        (accFacility) => accFacility.id === facility.id,
      )?.selected;
    });
    this.currentAccountValue!.facilities = facilities;
    this.facilitiesSubject.next(this.currentAccountValue!.facilities);
  }

  updateApps(apps: AccountAppDTO[]) {
    this.currentAccountValue!.apps = apps;

    const currentApp = apps.find((app) => app.id === this.currentAppValue?.id);
    if (currentApp) {
      this.setApp(currentApp);
    } else {
      this.setApp(apps[0]);
    }
  }

  updateGroups(groups: AccountUserGroupDTO[]) {
    this.currentAccountValue!.groups = groups;
  }

  initializeApp(account: AvailableAccount) {
    const apps = account.apps;
    if (this.LocalStorageService.appID) {
      const app = account.apps!.find(
        (app) => app.id === this.LocalStorageService.appID,
      );
      if (app) {
        this.setApp(app);
      }
    } else if (apps!.length) {
      const app = apps![0];
      this.setApp(app);
    }
  }

  setSelectedFacilities() {
    this.LocalStorageService.setSelectedFacilities(this.selectedFacilities!);
  }

  setSelectAllFacilities() {
    console.log("setSelectAllFacilities");
    this.facilitiesValue?.forEach((facility) => {
      facility.selected = true;
    });
    this.setSelectedFacilities();
  }

  setSelectedNoFacilities() {
    console.log("setSelectedNoFacilities");
    this.facilitiesValue?.forEach((facility) => {
      if(this.selectedFacilities?.length == 1){
        return;
      }
      facility.selected = false;
    });
    this.setSelectedFacilities();
  }
}
